/**
 * PATH: packages/email-hub/src/_internal/lib/emailSurfaceTheme.ts
 * FILE_ID: EMAILHUB-SURFACE-THEME-001
 * PURPOSE: Resolve per-user Email Hub background prefs into CSS vars.
 * WHY:     Operators want Kanban-style workspace tinting so Email is
 *          visually distinct; reuse Kanban's light-first theme engine
 *          without writing Board.config_json (email prefs are per-user).
 * FLOW:
 *   layout_json.background → getBoardTheme(stub) → --email-* CSS vars
 * RELATED:
 *   - @quotech/ui/kanban theme.ts / boardBackground.ts
 *   - emailSurfaceBackground.ts (coerce — no kanban import)
 *   - _internal/stores/uiStore.ts (persists background on layout_json)
 */
import {
  getBoardTheme,
  themeCssVars,
  type BoardTheme,
} from '@quotech/ui/kanban'
import type { Board } from '@quotech/ui/kanban'
import {
  hasEmailBackground,
  type EmailSurfaceBackground,
} from './emailSurfaceBackground'

export type { EmailSurfaceBackground } from './emailSurfaceBackground'
export { coerceEmailBackground, hasEmailBackground } from './emailSurfaceBackground'

/**
 * Resolve Email background prefs via Kanban's getBoardTheme.
 * Uses a stub Board so JOB/TRELLO auto-hash theming never applies —
 * only explicit operator picks (preset / colour / image).
 */
export function getEmailSurfaceTheme(
  background: EmailSurfaceBackground | null | undefined,
): BoardTheme | null {
  if (!hasEmailBackground(background)) return null
  // kind=CUSTOM: getBoardTheme only auto-hashes JOB / TRELLO_MIRROR.
  const stub = {
    id: 'email-surface',
    kind: 'CUSTOM' as Board['kind'],
    config_json: { ...(background ?? {}) },
  } as Board
  return getBoardTheme(stub)
}

/**
 * CSS custom properties for the Email content shell.
 * Maps Kanban tokens → email-named aliases so chrome can opt in
 * without colliding with an open Kanban board on the same page.
 *
 * Image mode: shell gets scrim+url; list chrome uses a translucent
 * `--email-pane-fill` so the photo shows through while text stays
 * readable. Message card stays opaque white (`--email-card`).
 */
export function emailSurfaceCssVars(
  theme: BoardTheme | null,
): Record<string, string> {
  if (!theme) return {}
  const base = themeCssVars(theme)
  const out: Record<string, string> = {
    ['--email-canvas' as string]: theme.bg,
    ['--email-pane' as string]: theme.lane_bg,
    ['--email-card' as string]: theme.card_bg,
    ['--email-accent' as string]: theme.accent,
    // Colour-only: opaque pane. Image mode overrides below.
    ['--email-pane-fill' as string]: theme.lane_bg,
  }
  if (base['--card-fg']) out['--email-fg' as string] = base['--card-fg']
  if (base['--card-fg-muted']) out['--email-fg-muted' as string] = base['--card-fg-muted']
  if (theme.bg_image_url || base.backgroundImage) {
    // Stronger light scrim than Kanban default — Email panes are denser
    // and need more wash so list text stays WCAG-legible over photos.
    const url = theme.bg_image_url
    const scrim =
      'linear-gradient(rgba(255,255,255,0.62), rgba(255,255,255,0.74))'
    out.backgroundImage = url
      ? `${scrim}, url("${url}")`
      : (base.backgroundImage as string)
    out.backgroundSize = 'cover'
    out.backgroundPosition = 'center'
    out.backgroundRepeat = 'no-repeat'
    out.backgroundAttachment = 'local'
    // Translucent list/filter chrome so the photo peeks through.
    out['--email-pane-fill' as string] =
      `color-mix(in srgb, ${theme.lane_bg} 70%, transparent)`
    out['--email-has-image' as string] = '1'
  } else {
    out.backgroundColor = theme.bg
  }
  return out
}
